import React, {useContext} from 'react'
import {useNavigate} from 'react-router-dom'
import {Row, Col, Card} from 'react-bootstrap'
import roleContext from '../../context/roleContext'
import {RoleContextValue} from '../../types/OtherTypes'
import {useAuth} from '../../modules/auth'

interface TimesheetOption {
  id: string
  title: string
  description: string
  icon: string
  path: string
  color: string
  roles: string[] // 'employee', 'manager', 'admin'
}

const TimesheetDashboard = () => {
  const navigate = useNavigate()
  let roleState: RoleContextValue = useContext(roleContext)
  const {currentUser} = useAuth()

  // Determine user role type
  // const roleName = roleState.roleName?.toLowerCase()
  // const isSuperAdmin = roleState.roleName === 'Super Admin'
  // const isAdmin = roleName === 'admin'
  // const isManagerOrHr = currentUser?.isRmOrHr || false
  // const isEmployee = !isSuperAdmin && !isManagerOrHr
  const normalizedRoleName =
  roleState.roleName?.trim().toUpperCase().replace(/_/g, ' ')
  const isSuperAdmin = normalizedRoleName === 'SUPER ADMIN'
  const isAdmin = normalizedRoleName === 'ADMIN'
  const isManagerOrHr = currentUser?.isRmOrHr || false
  const isEmployee = !isSuperAdmin && !isAdmin && !isManagerOrHr


  // Define all timesheet options
  const timesheetOptions: TimesheetOption[] = [
    {
      id: 'my-timesheet',
      title: 'My Timesheet',
      description: 'Submit and manage your daily timesheet entries',
      icon: 'bi-calendar-check',
      path: '/my-timesheet',
      color: 'primary',
      roles: ['employee', 'manager'],
    },
    {
      id: 'weekend-work',
      title: 'Weekend Work',
      description: 'Request approval to work on weekends',
      icon: 'bi-calendar-plus',
      path: '/timesheet/weekend-work',
      color: 'primary',
      roles: ['employee', 'manager', 'admin'], // All users can REQUEST weekend work
    },
    {
      id: 'team-approvals',
      title: 'Team Approvals',
      description: 'Review and approve team member timesheets',
      icon: 'bi-check-circle',
      path: '/timesheet/approvals',
      color: 'success',
      roles: ['manager', 'admin'],
    },
    {
      id: 'weekend-approvals',
      title: 'Weekend Approvals',
      description: 'Approve weekend work requests from team',
      icon: 'bi-calendar-check',
      path: '/timesheet/weekend-work/approvals',
      color: 'success',
      roles: ['manager', 'admin'],
    },
    //Timesheet inside report can be Hide
    // {
    //   id: 'reports',
    //   title: 'Reports',
    //   description: 'View comprehensive timesheet reports and analytics',
    //   icon: 'bi-graph-up',
    //   path: '/timesheet/reports',
    //   color: 'info',
    //   roles: ['manager', 'admin'],
    // },
    {
      id: 'impediments',
      title: 'Impediments',
      description: 'Track blockers and issues affecting work',
      icon: 'bi-exclamation-triangle',
      path: '/timesheet/impediments',
      color: 'danger',
      roles: ['manager', 'admin'], // Only managers and admins can see this
    },
    {
      id: 'master-data',
      title: 'Master Data',
      description: 'Manage project codes, tools, and task types',
      icon: 'bi-gear',
      path: '/timesheet/master-data',
      color: 'warning',
      roles: ['admin'],
    },
  ]

  // Filter options based on user role
  const getFilteredOptions = (): TimesheetOption[] => {
    if (isSuperAdmin || isAdmin) {
      return timesheetOptions.filter((opt) => opt.roles.includes('admin'))
    } else if (isManagerOrHr) {
      return timesheetOptions.filter((opt) => opt.roles.includes('manager'))
    } else {
      return timesheetOptions.filter((opt) => opt.roles.includes('employee'))
    }
  }

  const filteredOptions = getFilteredOptions()
  
  // Debug filtered options

  const handleCardClick = (path: string) => {
    navigate(path)
  }

  const getCardColorClass = (color: string): string => {
    const colorMap: {[key: string]: string} = {
      primary: 'border-primary',
      success: 'border-success',
      info: 'border-info',
      warning: 'border-warning',
      danger: 'border-danger',
    }
    return colorMap[color] || 'border-primary'
  }

  const getIconColorClass = (color: string): string => {
    const colorMap: {[key: string]: string} = {
      primary: 'text-primary',
      success: 'text-success',
      info: 'text-info',
      warning: 'text-warning',
      danger: 'text-danger',
    }
    return colorMap[color] || 'text-primary'
  }

  const getRoleTitle = (): string => {
    if (isSuperAdmin) return 'Super Admin'
    if (isAdmin) return 'Admin'
    if (isManagerOrHr) return 'Manager / HR'
    return 'Employee'
  }

  const getUserDisplayName = (): string => {
    if (currentUser?.firstName && currentUser?.lastName) {
      return `${currentUser.firstName} ${currentUser.lastName}`
    }
    if (currentUser?.firstName) return currentUser.firstName
    if (currentUser?.email) return currentUser.email
    return 'User'
  }

  return (
    <>
      <Row className='mb-4'>
        <Col md={12}>
          <div className='card'>
            <div className='card-header border-0 pt-4 pb-2'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bold fs-3 mb-1'>Timesheet Module</span>
                <span className='text-muted mt-1 fw-semibold fs-7'>
                  Welcome, {getUserDisplayName()} ({getRoleTitle()})
                </span>
              </h3>
            </div>
            <div className='card-body py-2'>
              <p className='text-gray-600 fs-7 mb-0'>
                Select an option below to manage your timesheet activities. Each section provides
                specific tools and features based on your role.
              </p>
            </div>
          </div>
        </Col>
      </Row>

      <Row className='g-6 g-xl-9'>
        {filteredOptions.map((option) => (
          <Col key={option.id} md={6} lg={4} xl={3}>
            <Card
              className={`card-xl-stretch cursor-pointer hover-elevate-up transition-all ${getCardColorClass(
                option.color
              )} border-2`}
              onClick={() => handleCardClick(option.path)}
              style={{
                transition: 'all 0.3s ease',
                minHeight: '140px',
              }}
            >
              <Card.Body className='d-flex flex-column justify-content-between p-5'>
                <div className='d-flex flex-column'>
                  <div className='mb-2'>
                    <h3 className='card-title fw-bold mb-2 fs-5'>{option.title}</h3>
                    <p className='text-gray-600 fw-semibold fs-7 mb-0'>{option.description}</p>
                  </div>
                </div>
                <div className='d-flex align-items-center mt-2'>
                  <span className={`fw-bold fs-7 ${getIconColorClass(option.color)}`}>
                    Click to access →
                  </span>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Quick Stats Row (Optional - can be enabled later) */}
      {isEmployee && (
        <Row className='mt-5'>
          <Col md={12}>
            <div className='card bg-light-primary'>
              <div className='card-body py-3'>
                <div>
                  <h6 className='mb-1 fw-bold'>Quick Tip</h6>
                  <p className='text-gray-700 mb-0 fs-7'>
                    Remember to submit your timesheet daily before end of day. Contact your manager
                    if you need to modify submitted entries.
                  </p>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      )}

      {(isManagerOrHr || isSuperAdmin) && (
        <Row className='mt-5'>
          <Col md={12}>
            <div className='card bg-light-info'>
              <div className='card-body py-3'>
                <div>
                  <h6 className='mb-1 fw-bold'>Quick Actions</h6>
                  <p className='text-gray-700 mb-0 fs-7'>
                    {isSuperAdmin
                      ? 'Manage team approvals, generate reports, and configure master data for the timesheet system.'
                      : 'Submit your timesheet, approve team submissions, and view comprehensive team reports all in one place.'}
                  </p>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      )}
    </>
  )
}

export default TimesheetDashboard

